import { useEffect, useRef, useState, useMemo } from "react";

import config from "config/config";
import { Button } from "components/button/Button";
import { useRootStore } from "store";
import { Thread } from "models/inbox/inbox";
import ThreadPreview from "./threadPreview/ThreadPreview";

import "./ThreadList.scss";

type Props = {
  threads: Thread[];
  shouldScroll: boolean;
  threadsLeftToLoad: number;
  loadMore: () => void;
};

export default function ThreadList({
  threads,
  shouldScroll,
  threadsLeftToLoad,
  loadMore
}: Props) {
  const { loadInbox, setThreadStatus } = useRootStore((state) => state.inbox);
  const user = useRootStore((state) => state.user);
  const lastThreadRef = useRef<HTMLDivElement>(null);

  const [focusedThreadIndex, setFocusedThreadIndex] = useState(
    config.defaults.pageSize - 1
  );

  const userInfoId = user.userInfo ? user.userInfo.id : "";

  const nextTreadsToLoadCount = Math.min(
    threadsLeftToLoad,
    config.defaults.pageSize
  );

  const threadsWithMeta = useMemo(() => {
    return [...threads]
      .sort((a, b) =>
        b.lastMessage.createdAt > a.lastMessage.createdAt ? 1 : -1
      )
      .map((thread) => ({
        ...thread,
        subscriber: thread.subscribers.find((sub) => sub.id === userInfoId),
        threadLink: `/inbox/thread/${encodeURIComponent(thread.threadId)}`
      }));
  }, [threads, userInfoId]);

  useEffect(() => {
    if (
      lastThreadRef &&
      lastThreadRef.current &&
      threads.length > config.defaults.pageSize &&
      shouldScroll
    ) {
      lastThreadRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      setFocusedThreadIndex(focusedThreadIndex + nextTreadsToLoadCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastThreadRef.current, nextTreadsToLoadCount]);

  if (threads.length === 0) {
    return (
      <>
        <p>
          Hoppsan, här var det tomt. Klicka på Ny konversation för att skicka
          ett meddelande.
        </p>
        <Button variant="outline" size="sm" onClick={loadInbox}>
          Ladda om inkorgen
        </Button>
      </>
    );
  }

  return (
    <>
      <ul className="threadWrapper">
        {threadsWithMeta.map((thread, index) => {
          return (
            <div
              ref={index === focusedThreadIndex ? lastThreadRef : null}
              key={`${thread.threadId}-${index}`}
            >
              <ThreadPreview
                isSubscribing={thread.subscriber !== undefined}
                thread={thread}
                threadLink={thread.threadLink}
                handleThreadListAction={(action) =>
                  setThreadStatus(thread.threadId, action)
                }
              />
            </div>
          );
        })}
      </ul>

      {threadsLeftToLoad > 0 && (
        <div className="moreItem">
          <Button variant="outline" size="sm" onClick={loadMore}>
            Visa nästa {nextTreadsToLoadCount} konversationer
          </Button>
        </div>
      )}
    </>
  );
}
